import React from "react";
import { ConversationType } from "../../constants/chat.constant";
import { ChatContainer } from "./ChatContainer";
import { CitationDetailsModal } from "../modal/citationDetailsModal";
import { ChatInputRef } from "./chat-input";
import placeholderChatImg from "@/assets/not-found-1.svg";

/**
 * Props for the ChatLayout component
 */
interface ChatLayoutProps {
  /** Type of conversation */
  conversationType: ConversationType;
  /** Messages to display in the conversation */
  displayConversation: any;
  /** Whether a response is being generated */
  isResponseGenerating: boolean;
  /** Whether messages are loading */
  isLoading: boolean;
  /** ID of the conversation currently streaming */
  streamingConversationId: string | null;
  /** ID of the selected conversation */
  selectedConversationId?: string;
  /** Whether sources modal is open */
  sourcesOpen: boolean;
  /** Index of the active message */
  activeMessageIndex: number | null;
  /** Current input content */
  content: string;
  /** Selected AI model */
  selectedModel: string;
  /** Model temperature setting */
  modelTemperature: number;
  /** Model reasoning effort setting */
  modelEffort: number;
  /** Whether conversation has messages */
  hasMessages: boolean;
  /** Whether to show upcoming changes */
  showUpcoming: boolean;
  /** Whether chat history is loading */
  isChatHistoryLoading: boolean;
  /** Whether citation is loading */
  isCitationLoading: boolean;
  /** ID of selected citation */
  selectedCitationId: number | null;
  /** Callback fired when conversation type changes */
  onConversationTypeChange: (type: ConversationType) => void;
  /** Callback fired when sources are opened */
  onOpenSources: (sources: any[], messageIndex: number) => void;
  /** Callback fired when sources are closed */
  onCloseSources: () => void;
  /** Callback fired when citation details are opened */
  onOpenCitationDetails: (citation: any) => void;
  /** Callback fired when message is sent */
  onSend: (message: any) => void;
  /** Callback fired when typing starts */
  onStartTyping: () => void;
  /** Callback fired when typing stops */
  onStopTyping: () => void;
  /** Callback fired when input changes */
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Callback fired on keyboard events */
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Callback fired when model changes */
  onModelChange: (model: string) => void;
  /** Callback fired when temperature changes */
  onTemperatureChange: (temperature: number) => void;
  /** Callback fired when reasoning effort changes */
  onReasoningEffortChange: (effort: number) => void;
  /** Callback fired to start new chat */
  onNewChat: () => void;
  /** Callback to set show upcoming state */
  setShowUpcoming: (show: boolean) => void;
  /** Callback to refetch folders */
  refetchFolders: () => void;
  /** Callback fired to cancel streaming */
  onCancelStream: () => void;
  /** Desktop suggestion prompts */
  suggestions: Array<{ prompt: string }>;
  /** Mobile suggestion prompts */
  mobileSuggestions: Array<{ prompt: string }>;
  /** Citation sources */
  sources: any[];
  /** Citation details data */
  citationDetails: any;
  /** Whether citation details modal is open */
  citationDetailsOpen: boolean;
  /** Callback fired when citation details are closed */
  onCloseCitationDetails: () => void;
  /** Selected conversation data */
  selectedConversation: any;
  /** Active conversation data */
  activeConversation: any;
  /** Function to get files for a conversation */
  getFilesForConversation?: (conversationId: string) => File[];
  /** Function to update files for a conversation */
  updateFilesForConversation?: (conversationId: string, files: File[]) => void;
  /** Whether sensitive chat mode is active */
  isSensitive?: boolean;
  /** Callback fired when sensitive mode is toggled */
  onToggleSensitive?: () => void;
  /** Whether sensitive mode toggle is disabled */
  isSensitiveDisabled?: boolean;
  /** Ref to the chat input component */
  chatInputRef?: React.RefObject<ChatInputRef>;
}

/**
 * ChatLayout component is the main layout wrapper for the chat interface.
 * Conditionally renders ChatContainer when a conversation is selected, or a placeholder
 * when no conversation is selected. Manages citation details modal.
 * 
 * @component
 * @param {ChatLayoutProps} props - Component props
 * @returns {JSX.Element} Chat layout with conditional content rendering
 * 
 * @example
 * ```tsx
 * <ChatLayout
 *   conversationType={ConversationType.STANDARD}
 *   displayConversation={messages}
 *   onSend={handleSend}
 * />
 * ```
 */
export const ChatLayout: React.FC<ChatLayoutProps> = ({
  conversationType,
  displayConversation,
  isResponseGenerating,
  isLoading,
  streamingConversationId,
  selectedConversationId,
  sourcesOpen,
  activeMessageIndex,
  content,
  selectedModel,
  modelTemperature,
  modelEffort,
  hasMessages,
  showUpcoming,
  isChatHistoryLoading,
  isCitationLoading,
  selectedCitationId,
  onConversationTypeChange,
  onOpenSources,
  onCloseSources,
  onOpenCitationDetails,
  onSend,
  onStartTyping,
  onStopTyping,
  onInputChange,
  onKeyDown,
  onModelChange,
  onTemperatureChange,
  onReasoningEffortChange,
  onNewChat,
  setShowUpcoming,
  refetchFolders,
  suggestions,
  mobileSuggestions,
  sources,
  citationDetails,
  citationDetailsOpen,
  onCloseCitationDetails,
  selectedConversation,
  activeConversation,
  getFilesForConversation,
  updateFilesForConversation,
  isSensitive = false,
  onToggleSensitive,
  isSensitiveDisabled = false,
  onCancelStream,
  chatInputRef,
}) => {
  return (
    <>
      <div className="flex flex-col h-full w-full bg-secondary-500/5 overflow-hidden">
        <div className={`flex-1 w-full overflow-y-auto overflow-x-hidden flex`}>
          {selectedConversation ||
          (!selectedConversationId && activeConversation !== null) ? (
            <ChatContainer
              ref={chatInputRef}
              displayConversation={displayConversation}
              isResponseGenerating={isResponseGenerating}
              isLoading={isLoading}
              streamingConversationId={streamingConversationId}
              selectedConversationId={selectedConversationId}
              sourcesOpen={sourcesOpen}
              activeMessageIndex={activeMessageIndex}
              content={content}
              selectedModel={selectedModel}
              modelTemperature={modelTemperature}
              modelEffort={modelEffort}
              hasMessages={hasMessages}
              showUpcoming={showUpcoming}
              conversationType={conversationType}
              isCitationLoading={isCitationLoading}
              selectedCitationId={selectedCitationId}
              onOpenSources={onOpenSources}
              onCloseSources={onCloseSources}
              onOpenCitationDetails={onOpenCitationDetails}
              onSend={onSend}
              onStartTyping={onStartTyping}
              onStopTyping={onStopTyping}
              onInputChange={onInputChange}
              onKeyDown={onKeyDown}
              onModelChange={onModelChange}
              onTemperatureChange={onTemperatureChange}
              onReasoningEffortChange={onReasoningEffortChange}
              onNewChat={onNewChat}
              setShowUpcoming={setShowUpcoming}
              suggestions={suggestions}
              mobileSuggestions={mobileSuggestions}
              sources={sources}
              citationDetails={citationDetails}
              citationDetailsOpen={citationDetailsOpen}
              onCloseCitationDetails={onCloseCitationDetails}
              getFilesForConversation={getFilesForConversation}
              updateFilesForConversation={updateFilesForConversation}
              isSensitive={isSensitive}
              onToggleSensitive={onToggleSensitive}
              isSensitiveDisabled={isSensitiveDisabled}
              onCancelStream={onCancelStream}
            />
          ) : (
            <div className="h-full overflow-x-hidden flex items-center justify-center">
              {/* Placeholder container when no conversation is selected */}
              <div className="placeholder-chat-img flex flex-col items-center justify-center">
                <img
                  src={placeholderChatImg}
                  alt="No conversation"
                  className="w-[300px]"
                />
                <div className="mt-10 text-text-200">
                  No conversation selected.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <CitationDetailsModal
        open={citationDetailsOpen}
        citation={citationDetails}
        onClose={onCloseCitationDetails}
        loading={false}
      />
    </>
  );
};
